/**
 * Shared renderer value semantics.
 *
 * DOM and string rendering must recognize the same branded template,
 * conditional, and repeat values. Keeping those brands and their resolution
 * rules here prevents the browser and no-DOM paths from drifting. Unknown part
 * types retain the historical node-like fallback, while runtime contract
 * violations keep their stable RT error codes. This module never reads DOM
 * globals, so the string renderer remains safe to load in SSR environments.
 */
import { isSignal } from './signal.js';
export const INSTANCE_SYMBOL = Symbol.for('hydrogen.templateInstance');
const REPEAT_SYMBOL = Symbol.for('hydrogen.repeat');
const CONDITIONAL_SYMBOL = Symbol.for('hydrogen.conditional');
const SPREAD_SYMBOL = Symbol.for('hydrogen.spread');
const REF_SYMBOL = Symbol.for('hydrogen.ref');
const UNTIL_SYMBOL = Symbol.for('hydrogen.until');
const LIVE_SYMBOL = Symbol.for('hydrogen.live');
const CACHE_SYMBOL = Symbol.for('hydrogen.cache');
export function runtimeError(code, message) {
    const error = new Error(`${code}: ${message}`);
    error.code = code;
    return error;
}
export function assertNeverPartType(type) {
    throw runtimeError('RT401', `Unknown part type: ${String(type)}`);
}
export function partMarker(index) {
    return `h:${index}`;
}
export function attrMarker(index) {
    return `__h:${index}__`;
}
/**
 * Element-part marker: emitted as a bare, valueless attribute NAME
 * (`<div __h:0__>`), not an attribute VALUE placeholder. Shares `attrMarker`'s
 * textual format today because both live in the same attribute-name
 * namespace during DOM marker discovery, but the two are named separately
 * since they mark structurally different holes.
 */
export function elementMarker(index) {
    return `__h:${index}__`;
}
export function endMarker(index) {
    return `/h:${index}`;
}
/** Escapes a string for safe interpolation into a `RegExp` pattern source. */
export function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/**
 * Splits a composite attribute's original STATIC source into the segments
 * between each contributing hole's marker (G4). Both renderers must derive
 * these segments from the untouched static source exactly once — never by
 * repeatedly `.replace()`-ing an already-substituted runtime string, which
 * would let a later hole's resolved value collide with an earlier hole's
 * marker text (or vice versa) whenever a resolved value happens to contain
 * literal marker-shaped text.
 */
export function splitCompositeSegments(sourceValue, bindingIndices) {
    const pattern = new RegExp(bindingIndices.map((index) => escapeRegExp(attrMarker(index))).join('|'));
    return sourceValue.split(pattern);
}
/**
 * Bare attribute names cannot contain whitespace, quotes, `=`, `<`, `>`, or
 * `/` — any of those would let a presence token (G1) or attribute-bag key
 * (spread()/plain object) escape the attribute-NAME position. The string
 * renderer needs this to keep injected names out of SSR markup; the DOM
 * renderer needs the SAME check so both paths reject the same inputs with
 * RT406 instead of the browser surfacing a raw InvalidCharacterError (or the
 * test shim silently accepting what a real browser would throw on).
 */
const VALID_ATTRIBUTE_NAME_RE = /^[^\s"'<>/=]+$/;
/** Throws RT406 for any presence-token or attribute-bag name that could not
 * be emitted as a single, unambiguous bare or `name="value"` attribute. */
export function assertValidAttributeName(name) {
    if (!VALID_ATTRIBUTE_NAME_RE.test(name)) {
        throw runtimeError('RT406', `Invalid attribute name "${name}" — element-part presence tokens and attribute-bag keys ` +
            'cannot contain whitespace, quotes, "=", "<", ">", or "/".');
    }
    return name;
}
export function isAttributePartType(type) {
    switch (type) {
        case 'attribute': // ubs:ignore -- grouped cases return from the shared branch
        case 'property': // ubs:ignore -- grouped cases return from the shared branch
        case 'boolean-attribute': // ubs:ignore -- grouped cases return from the shared branch
        case 'event': // ubs:ignore -- grouped cases return from the shared branch
            return true;
        case 'node':
        case 'text':
        case 'element':
        case 'conditional':
        case 'repeat':
        case 'keyed-sequence':
        case 'raw-html':
            return false;
        default:
            return false;
    }
}
/** Element parts have no comment marker and are never attribute-family. */
export function isElementPartType(type) {
    return type === 'element';
}
export function isNode(value) {
    return !!(value && typeof value === 'object' && 'nodeType' in value);
}
export function isTemplateInstanceValue(value) {
    return !!(value &&
        typeof value === 'object' &&
        value[INSTANCE_SYMBOL]);
}
export function resolveValue(value) {
    return isSignal(value) ? value() : value;
}
export function textValue(value) {
    if (value === null || value === undefined || value === false)
        return '';
    return String(value);
}
export function isRepeat(value) {
    return !!(value &&
        typeof value === 'object' &&
        value[REPEAT_SYMBOL]);
}
export function isConditional(value) {
    return !!(value &&
        typeof value === 'object' &&
        value[CONDITIONAL_SYMBOL]);
}
export function isSpread(value) {
    return !!(value &&
        typeof value === 'object' &&
        value[SPREAD_SYMBOL]);
}
export function isRef(value) {
    return !!(value &&
        typeof value === 'object' &&
        value[REF_SYMBOL]);
}
export function isUntil(value) {
    return !!(value &&
        typeof value === 'object' &&
        value[UNTIL_SYMBOL]);
}
export function isLive(value) {
    return !!(value &&
        typeof value === 'object' &&
        value[LIVE_SYMBOL]);
}
export function isCache(value) {
    return !!(value &&
        typeof value === 'object' &&
        value[CACHE_SYMBOL]);
}
/**
 * True when a row function can observe its position — it declares the index as
 * its second formal parameter.
 *
 * A row that never asks for its index cannot render differently because a
 * sibling was removed, so a keyed repeat may keep such a row across a shift
 * without re-running it (#1330). Deleting every tenth of 1,000 rows moves ~900
 * survivors by one place; before this, all 900 re-ran to rebuild byte-identical
 * output. Position-dependence is therefore part of the row's declared shape,
 * exactly as identity is already the contract for the item (#677).
 *
 * `Function.length` counts formal parameters before the first defaulted or rest
 * one, so a row written `(item, ...rest) => rest[0]` or `(item, index = 0)`
 * reads as index-blind. `renderRow` calls those with ONE argument rather than
 * two, which turns the misdeclaration into `undefined` on the very first render
 * instead of a correct-looking index that silently goes stale after the first
 * shift. Solid's `mapArray` makes the same trade for the same reason.
 */
export function rowReadsIndex(templateFn) {
    return templateFn.length >= 2;
}
/**
 * Invokes a keyed row's template function under the arity contract above.
 *
 * Every renderer — DOM, string, stream, hydration adoption — routes through
 * here so a row is called with the same argument count on every path. A row
 * that reads an undeclared second argument would otherwise see the index on
 * the server and `undefined` on the client, which is a hydration mismatch.
 */
export function renderRow(templateFn, item, index) {
    const fn = templateFn;
    return rowReadsIndex(templateFn) ? fn(item, index) : fn(item);
}
/** Describes a keyed sequence part. */
export function repeat(items, keyFn, templateFn) {
    return {
        [REPEAT_SYMBOL]: true,
        items,
        keyFn,
        templateFn,
    };
}
/** Describes a conditional node part. */
export function conditional(condition, truthy, falsy = '') {
    return {
        [CONDITIONAL_SYMBOL]: true,
        condition,
        truthy,
        falsy,
    };
}
/**
 * Marks a plain object as an attribute/property/event bag for an element
 * part. Key routing mirrors attribute-part prefixes (`@name`, `.name`,
 * `?name`, plain) and is resolved by the DOM/string renderers.
 */
export function spread(props) {
    return {
        [SPREAD_SYMBOL]: true,
        props,
    };
}
/**
 * Marks an element-part value as a DOM reference sink: either a callback
 * invoked with the element (and `null` on detach/destroy) or a `createRef()`
 * object whose `.current` tracks the live element.
 */
export function ref(target) {
    return {
        [REF_SYMBOL]: true,
        target,
    };
}
/** Creates a mutable ref object compatible with `ref()`. */
export function createRef() {
    return { current: null };
}
/**
 * Renders the first non-thenable value immediately; later-settling promises
 * commit only if their position is higher priority (lower index) than
 * whatever is currently committed.
 */
export function until(...values) {
    return {
        [UNTIL_SYMBOL]: true,
        values,
    };
}
/**
 * Bypasses the attribute/property dirty-check memo and compares against the
 * element's current live state instead, so external DOM mutation (e.g. a
 * native `<input>` edit) does not get masked by a stale committed value.
 */
export function live(value) {
    return {
        [LIVE_SYMBOL]: true,
        value,
    };
}
/**
 * Keeps a per-part cache of materialized template instances keyed by
 * template identity, so switching between cached templates re-inserts nodes
 * instead of destroying and rebuilding them.
 */
export function cache(value) {
    return {
        [CACHE_SYMBOL]: true,
        value,
    };
}
//# sourceMappingURL=render-values.js.map